import { Web3Provider } from '@ethersproject/providers';
import { Connector, ConnectorEvent, ConnectorUpdateData } from '@usedapp/core';

declare global {
  interface Window {
    ethereum?: {
      providers?: Array<{
        isRabby?: boolean;
        request: (args: {
          method: string;
          params?: unknown[];
        }) => Promise<unknown>;
      }>;
      isRabby?: boolean;
      request: (args: {
        method: string;
        params?: unknown[];
      }) => Promise<unknown>;
    };
    rabby?: {
      request: (args: {
        method: string;
        params?: unknown[];
      }) => Promise<unknown>;
    };
  }
}

type ExternalProvider = {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
};

/** EIP-1193 provider with event subscription (accountsChanged, chainChanged) */
type ProviderWithEvents = ExternalProvider & {
  on?: (event: string, callback: (...args: unknown[]) => void) => void;
  removeListener?: (
    event: string,
    callback: (...args: unknown[]) => void,
  ) => void;
};

const RABBY_DOWNLOAD_URL = 'https://rabby.io';

function getRawRabbyProvider(): ExternalProvider | undefined {
  if (typeof window === 'undefined') return undefined;

  const win = window as Window;
  const raw =
    win.rabby ??
    win.ethereum?.providers?.find(p => (p as { isRabby?: boolean })?.isRabby) ??
    (win.ethereum?.isRabby ? win.ethereum : undefined);

  return raw as ExternalProvider | undefined;
}

/**
 * Create Web3Provider only after we have chainId from the wallet.
 * Creating it with explicit network avoids ethers "could not detect network" (NETWORK_ERROR)
 * which occurs when using injected providers with automatic network detection.
 */
function createWeb3Provider(
  raw: ExternalProvider,
  chainId: number,
): Web3Provider {
  const network = {
    chainId,
    name: chainId === 1 ? 'mainnet' : `chain-${chainId}`,
  };
  return new Web3Provider(
    raw as unknown as import('@ethersproject/providers').ExternalProvider,
    network,
  );
}

export class RabbyConnector implements Connector {
  public provider?: Web3Provider;

  public readonly name = 'Rabby';

  readonly update = new ConnectorEvent<ConnectorUpdateData>();

  private rawProvider?: ExternalProvider;

  private unsubscribeProviderEvents: (() => void) | null = null;

  private ensureRaw(): ExternalProvider | undefined {
    if (this.rawProvider) return this.rawProvider;
    this.rawProvider = getRawRabbyProvider();
    return this.rawProvider;
  }

  /**
   * Subscribe to EIP-1193 accountsChanged and chainChanged so app state
   * stays in sync when the user switches accounts or networks in Rabby.
   */
  private subscribeToProviderEvents(raw: ProviderWithEvents): void {
    this.unsubscribeProviderEvents?.();

    if (typeof raw.on !== 'function') return;

    const emitUpdate = async (): Promise<void> => {
      if (!this.rawProvider) return;
      try {
        const chainIdRaw = await this.rawProvider.request({
          method: 'eth_chainId',
        });
        const chainId =
          typeof chainIdRaw === 'string'
            ? parseInt(chainIdRaw, 16)
            : Number(chainIdRaw);
        const accounts = (await this.rawProvider.request({
          method: 'eth_accounts',
        })) as string[];
        this.update.emit({ chainId, accounts });
      } catch (e) {
        console.debug('RabbyConnector: failed to emit update', e);
      }
    };

    const onAccountsChanged = (): void => {
      emitUpdate().catch(e => console.debug('RabbyConnector: emitUpdate', e));
    };
    const onChainChanged = (): void => {
      emitUpdate().catch(e => console.debug('RabbyConnector: emitUpdate', e));
    };

    raw.on('accountsChanged', onAccountsChanged);
    raw.on('chainChanged', onChainChanged);

    this.unsubscribeProviderEvents = (): void => {
      if (typeof raw.removeListener === 'function') {
        raw.removeListener('accountsChanged', onAccountsChanged);
        raw.removeListener('chainChanged', onChainChanged);
      }
      this.unsubscribeProviderEvents = null;
    };
  }

  async connectEagerly(): Promise<void> {
    const raw = this.ensureRaw();
    if (!raw) return;
    try {
      const chainIdRaw = await raw.request({ method: 'eth_chainId' });
      const chainId =
        typeof chainIdRaw === 'string'
          ? parseInt(chainIdRaw, 16)
          : Number(chainIdRaw);
      const accounts = (await raw.request({
        method: 'eth_accounts',
      })) as string[];
      if (!accounts?.length) return;
      this.provider = createWeb3Provider(raw, chainId);
      this.subscribeToProviderEvents(raw as ProviderWithEvents);
      this.update.emit({ chainId, accounts });
    } catch (e) {
      console.debug(e);
    }
  }

  async activate(): Promise<void> {
    const raw = this.ensureRaw();
    if (!raw) {
      if (typeof window !== 'undefined') {
        window.open(RABBY_DOWNLOAD_URL, '_blank', 'noopener,noreferrer');
      }
      throw new Error('Rabby Wallet is not installed');
    }
    try {
      const chainIdRaw = await raw.request({ method: 'eth_chainId' });
      const chainId =
        typeof chainIdRaw === 'string'
          ? parseInt(chainIdRaw, 16)
          : Number(chainIdRaw);
      const accounts = (await raw.request({
        method: 'eth_requestAccounts',
      })) as string[];
      this.provider = createWeb3Provider(raw, chainId);
      this.subscribeToProviderEvents(raw as ProviderWithEvents);
      this.update.emit({
        chainId,
        accounts,
      });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      throw new Error(`Could not activate connector: ${message}`);
    }
  }

  async deactivate(): Promise<void> {
    this.unsubscribeProviderEvents?.();
    this.unsubscribeProviderEvents = null;
    this.provider = undefined;
    this.rawProvider = undefined;
  }
}
